import { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";

//Props Validations
import PropTypes from "prop-types";

// import component
import Loader from "../../common/Loader";
import FilterAcrossProductsToggleComponent from "../../common/FilterAcrossProductsToggleComponent";
import Reset from "../../common/Reset";
import TTestDialog from "../../common/TTest/TTestDialog";
import AnalysisTypeSelector from "../../common/AnalysisTypeSelector";
import SignificanceToggleComponent from "components/common/SignificanceToggleComponent";
import CohensDButton from "components/common/CohensDButton";

// redux
import { useSelector, useDispatch } from "react-redux";
import {
    loadIsFragranceMetricsAnalysisResultLoadingState,
    loadFragranceMetricsAnalysisResultState,
    loadDefaultFragranceMetricsAnalysisResultState,
    loadDisableSignificanceToggleState,
    loadSignificanceToggleState,
    loadIsScoreDistributionShownOverallState,
    loadScoreDistributionSelectedVariableOverallState,
    loadScoreDistributionGroupsOverallState,
} from "store/features/analysisResultsSlice";

import { resetTTestStore } from "store/features/ttestSlice";
import { resetCohensDStore } from "store/features/cohensDSlice";

import GetFragranceMetricsResult from "utils/apis/GetFragranceMetricsResult";
import ViewToggle from "components/common/ViewToggle";
import InitialPopup from "../../common/InitialPopup";
import AnalysisTableTransposed from "../../common/analysisModule/AnalysisTableTransposed";
import AnalysisTransposedTableChart from "../../common/analysisModule/chart/AnalysisTransposedTableChart";
import DataPointSampling from "components/common/DataPointSampling";
import ScoreDistributionVariableSelect from "components/common/ScoreDistributionVariableSelect";

function PerformanceTab({ dataMappingData = {}, pageContext = "" }) {
    const navigate = useNavigate();

    const productsOrder = useSelector((state) => state.productsInfoStore.productsOrder);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;

    //REDUX
    const dispatch = useDispatch();
    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);
    const isConsumerFilterSelected = useSelector((state) => state.consumerFilterStore.filterSelectionStatus);

    const productsSecondaryDataState = useSelector((state) => state.analysisResultsStore.fragranceMetricsAnalysisResult);
    const isLoading = useSelector((state) => state.analysisResultsStore.isFragranceMetricsAnalysisResultLoading);
    const overallBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.overallBoxAnalysisSelection);
    const secondaryBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.secondaryBoxAnalysisSelection);
    const agreementBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.agreementBoxAnalysisSelection);
    const claimsBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.claimsBoxAnalysisSelection);
    const fragranceOverallBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.fragranceOverallBoxAnalysisSelection);

    const scoreDistributionSelectedVariableOverall = useSelector((state) => state.analysisResultsStore.scoreDistributionSelectedVariableOverall);
    const isScoreDistributionShownOverall = useSelector((state) => state.analysisResultsStore.isScoreDistributionShownOverall);
    const scoreDistributionGroupsOverall = useSelector((state) => state.analysisResultsStore.scoreDistributionGroupsOverall);
    const likertScaleData = useSelector((state) => state.analysisResultsStore.likertScaleData);

    // toggle table and chart
    const [isTableShown, setIsTableShown] = useState(true);

    // cohens d details
    const [cohensDResult, setCohensDResult] = useState([]);

    // t-test details
    const [tTestResult, setTTestResult] = useState([]);
    const [testLabel, setTestLabel] = useState("");

    // Analysis type
    const analysisTypeValue = useSelector((state) => state.analysisTypeSelectionStore.fragranceOverallTypeValue);
    const boxAnalysisTypeSelection = useSelector((state) => state.analysisTypeSelectionStore.fragranceOverallBoxAnalysisTypeSelection);

    const [isFisher, setIsFisher] = useState(false);
    const [legends, setLegends] = useState([]);

    // data for data point sampling
    const [data, setData] = useState({});

    const isOralCare = process.env?.REACT_APP_ORAL_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCleansing = process.env?.REACT_APP_SKIN_CLEANSING?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isUnileverFragranceHouse = process.env?.REACT_APP_UNILEVER_FRAGRANCE_HOUSE?.toLowerCase() === productCategoryData?.label?.toLowerCase();

    // % Score Distribution view is available only for Unilever Fragrance House
    const isScoreDistributionMode = isUnileverFragranceHouse && !isTableShown && !isScoreDistributionShownOverall && selectedStudy.length === 1 && analysisTypeValue.value === "mean";

    const clearResult = () => {
        if (isUnileverFragranceHouse) {
            dispatch(loadScoreDistributionSelectedVariableOverallState(""));
            dispatch(loadScoreDistributionGroupsOverallState([]));
            setData({});
        }
        setTTestResult([]);
        setTestLabel("");
        dispatch(resetTTestStore());
        dispatch(loadSignificanceToggleState(false));
        dispatch(loadDisableSignificanceToggleState(true));
        setCohensDResult([]);
        dispatch(resetCohensDStore());
    };

    useEffect(() => {
        if (isOralCare || isSkinCleansing) {
            if (!isLoading) {
                if (!isConsumerFilterSelected) {
                    InitialPopup.general();
                }
            }
        }
    }, [isLoading]);

    const clearTtest = (product, type) => {
        // clear t test
        if (type === "consumerFilterAll") {
            clearResult();
        } else if (type === "consumerFilter") {
            const tTestPro = tTestResult?.[0]?.colors?.map((datum) => datum.productCode) || [];
            const cfPc = product?.split("_")?.join(" ") || "";
            if (tTestPro.indexOf(cfPc) !== -1) {
                clearResult();
            }
        } else {
            clearResult();
        }
    };

    const getOverallResult = async ({ product = null, coreDataId = null, type = null, consumerFilter = null, pcs = [], selectedStudy = [], allAnalysisData = {}, fetchOnlyCurrent = false, boxAnalysisSelection = {} }) => {
        clearTtest(product, type);
        // current page data
        dispatch(loadIsFragranceMetricsAnalysisResultLoadingState(true));

        const fragranceMetricsResult = await GetFragranceMetricsResult({
            product,
            coreDataId,
            type,
            consumerFilter,
            pcs,
            selectedStudy,
            productsSecondaryDataState: allAnalysisData?.fragranceMetricsAnalysisResult || [],
            productsOrder,
            productCategoryId,
            boxAnalysisSelection: boxAnalysisSelection["fragranceOverall"] || {},
            studyTypeMethod: "clt",
        });
        if (fragranceMetricsResult) {
            await dispatch(loadFragranceMetricsAnalysisResultState([...fragranceMetricsResult]));
            if (!type) {
                dispatch(loadDefaultFragranceMetricsAnalysisResultState([...fragranceMetricsResult]));
            }
        }
        dispatch(loadIsFragranceMetricsAnalysisResultLoadingState(false));
        console.log("fetchOnlyCurrent", fetchOnlyCurrent);
    };

    useEffect(() => {
        async function fetchAnalysisResult() {
            // if (!productsSecondaryDataState.length) {
            await getOverallResult({
                product: null,
                coreDataId: null,
                type: null,
                consumerFilter: null,
                pcs: [],
                selectedStudy,
                allAnalysisData: [],
                fetchOnlyCurrent: true,
                boxAnalysisSelection: {
                    overall: overallBoxAnalysisSelection,
                    secondary: secondaryBoxAnalysisSelection,
                    agreement: agreementBoxAnalysisSelection,
                    claims: claimsBoxAnalysisSelection,
                    fragranceOverall: fragranceOverallBoxAnalysisSelection,
                },
            });
            // }
        }
        if (selectedStudy.length === 0) {
            navigate("/studies");
        } else {
            fetchAnalysisResult();
        }
    }, [selectedStudy]);

    const returnComponent = () => {
        if (Object.keys(dataMappingData).length !== 0 && dataMappingData?.columns) {
            if (isTableShown) {
                return (
                    <AnalysisTableTransposed
                        productsSecondaryData={productsSecondaryDataState}
                        handleFilterSubmit={getOverallResult}
                        selectedStudy={selectedStudy}
                        analysisLevelData={{
                            tableConfig: {
                                stickyColumns: ["Products", "Sample Size", "Consumer Filters"],
                            },
                            moduleName: "overall",
                            bottomLabelModuleName: "fragranceMetrics",
                            analysisTypeValue: analysisTypeValue,
                            boxAnalysisTypeSelection: boxAnalysisTypeSelection,
                            boxAnalysisSelection: fragranceOverallBoxAnalysisSelection,
                            mappings: dataMappingData.columns.filter((ele) => ele.macroCategory.toLowerCase().includes("overall")),
                            cohensDResult: cohensDResult,
                        }}
                        tTest={{
                            tTestLegends: legends,
                            isFisher: isFisher,
                            tTestResult: tTestResult,
                            testLabel: testLabel,
                        }}
                        isTableShown={isTableShown}
                        pageContext={pageContext}
                    />
                );
            } else {
                return (
                    <AnalysisTransposedTableChart
                        productsSecondaryData={productsSecondaryDataState}
                        handleFilterSubmit={getOverallResult}
                        selectedStudy={selectedStudy}
                        analysisLevelData={{
                            tableConfig: {
                                stickyColumns: ["Products", "Sample Size", "Consumer Filters"],
                            },
                            moduleName: "overall",
                            bottomLabelModuleName: "fragranceMetrics",
                            analysisTypeValue: analysisTypeValue,
                            boxAnalysisTypeSelection: boxAnalysisTypeSelection,
                            boxAnalysisSelection: fragranceOverallBoxAnalysisSelection,
                            mappings: dataMappingData.columns.filter((ele) => ele.macroCategory.toLowerCase().includes("overall")),
                            isScoreDistributionShown: isScoreDistributionShownOverall,
                            scoreDistributionGroups: scoreDistributionGroupsOverall,
                            scoreDistributionSelectedVariable: scoreDistributionSelectedVariableOverall,
                        }}
                        tTest={{
                            tTestLegends: legends,
                            isFisher: isFisher,
                            tTestResult: tTestResult,
                            testLabel: testLabel,
                        }}
                        isTableShown={isTableShown}
                    />
                );
            }
        }
        return null;
    };

    return (
        <>
            {isLoading && <Loader />}
            <div
                className="card-btns"
                style={{ padding: "0px 0px", margin: "10px 0px" }}
            >
                <div
                    className=" d-flex"
                    style={{ alignItems: "center" }}
                >
                    <div>
                        <ViewToggle
                            value={isTableShown}
                            setValue={setIsTableShown}
                            labels={["Table", "Chart"]}
                        />
                    </div>
                    <div
                        style={{
                            marginLeft: "1.5rem",
                            zIndex: "3",
                            width: "150px",
                        }}
                    >
                        <AnalysisTypeSelector
                            value={analysisTypeValue}
                            pageName="fragranceOverallAnalysis"
                            variableCategory={"Fragrance Metrics Analysis"}
                            mappings={dataMappingData.columns}
                            handleFilterSubmit={getOverallResult}
                            moduleName="overall"
                            clearResult={clearResult}
                            disabled={isScoreDistributionMode}
                        />
                    </div>
                    {isScoreDistributionMode && (
                        <>
                            <div
                                style={{
                                    marginLeft: "1.5rem",
                                    zIndex: "3",
                                    width: "150px",
                                }}
                            >
                                <ScoreDistributionVariableSelect
                                    dataMappingData={dataMappingData.columns.filter((ele) => ele.macroCategory.toLowerCase().includes("overall"))}
                                    productsSecondaryDataState={productsSecondaryDataState}
                                    scoreDistributionSelectedVariable={scoreDistributionSelectedVariableOverall}
                                    moduleName="overall"
                                />
                            </div>
                            <div
                                style={{
                                    marginLeft: "1.5rem",
                                }}
                            >
                                <DataPointSampling
                                    likertScaleData={likertScaleData}
                                    scoreDistributionSelectedVariable={scoreDistributionSelectedVariableOverall}
                                    moduleName="overall"
                                    data={data}
                                    setData={setData}
                                />
                            </div>
                        </>
                    )}
                </div>
                <div>
                    <SignificanceToggleComponent disabled={isScoreDistributionMode || !isTableShown} />
                    <FilterAcrossProductsToggleComponent />
                    <CohensDButton
                        analysisLevelData={{
                            moduleName: "overall",
                            dataMappingData: dataMappingData.columns.filter((ele) => ele.macroCategory.toLowerCase().includes("overall") || ele.macroCategory.toLowerCase().includes("concept")),
                            analysisTypeValue: analysisTypeValue,
                        }}
                        tabData={{
                            productsSecondaryDataState: productsSecondaryDataState,
                        }}
                        setCohensDResult={setCohensDResult}
                        disabled={isScoreDistributionMode || (!isTableShown && selectedStudy.length === 1 && analysisTypeValue.value === "mean")}
                    />
                    <TTestDialog
                        variableCategory="Fragrance Metrics Analysis"
                        module="clt"
                        functionsSettings={{
                            setIsFisher: setIsFisher,
                            setLegends: setLegends,
                            setTTestResult: setTTestResult,
                            setTestLabel: setTestLabel,
                            clearResult: clearResult,
                        }}
                        analysisLevelData={{
                            moduleName: "overall",
                            analysisTypeValue: analysisTypeValue,
                            boxAnalysisTypeSelection: boxAnalysisTypeSelection,
                            boxAnalysisSelection: fragranceOverallBoxAnalysisSelection,
                            mappings: dataMappingData.columns.filter((ele) => ele.macroCategory.toLowerCase().includes("overall")),
                        }}
                        tabData={{
                            productsSecondaryDataState: productsSecondaryDataState,
                        }}
                        tTestBtnDisabled={isScoreDistributionMode}
                    />
                    <Reset clearTTestResult={clearResult} />
                </div>
            </div>
            <div
                style={{
                    minHeight: "calc(100vh - 300px)",
                }}
            >
                {returnComponent()}
            </div>
            {isUnileverFragranceHouse && !isTableShown && selectedStudy.length === 1 && analysisTypeValue.value === "mean" && (
                <div
                    style={{
                        width: "200px",
                        marginTop: "12px",
                    }}
                >
                    <ViewToggle
                        value={isScoreDistributionShownOverall}
                        setValue={(value) => dispatch(loadIsScoreDistributionShownOverallState(value))}
                        labels={["Mean", "% Score Distribution"]}
                    />
                </div>
            )}
            {cohensDResult.length > 0 && <p style={{ fontSize: "12px", marginTop: "6px" }}>Note: Cohen&apos;s d is not visually represented in the chart view</p>}
        </>
    );
}

// Typechecking props for the PerformanceTab
PerformanceTab.propTypes = {
    dataMappingData: PropTypes.object,
    pageContext: PropTypes.string,
    // onTTestDataReceived: PropTypes.func,
};

export default PerformanceTab;
